import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { TrackMap } from '../components/maps/OsmMap';
import { fmtEur } from '../lib/constants';
import { Loader2, PackageX, Check, Clock, ChefHat, Bike, ShoppingBag, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = {
  domicilio: ['pending', 'accepted', 'preparing', 'ready', 'in_transit', 'delivered'],
  asporto: ['pending', 'accepted', 'preparing', 'ready', 'delivered'],
};
const STEP_ICON = { pending: Clock, accepted: Check, preparing: ChefHat, ready: ShoppingBag, in_transit: Bike, delivered: MapPin };

export default function TrackingPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/offsite/track/${token}`);
      setOrder(data); setState('ok');
    } catch (e) { setState((s) => (s === 'ok' ? 'ok' : 'error')); }
  }, [token]);

  useEffect(() => {
    fetchOrder();
    const id = setInterval(fetchOrder, 5000); // light polling for live updates
    return () => clearInterval(id);
  }, [fetchOrder]);

  if (state === 'loading') {
    return <div className="min-h-dvh flex items-center justify-center bg-background text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> {t('publicMenu.loading')}</div>;
  }
  if (state === 'error' || !order) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-background text-center px-6">
        <PackageX className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <div className="font-display text-xl font-bold">{t('offsite.trackNotFound')}</div>
      </div>
    );
  }

  const steps = STEPS[order.type] || STEPS.domicilio;
  const rejected = order.status === 'rejected';
  const currentIdx = steps.indexOf(order.status);
  const statusAt = (st) => { const h = (order.history || []).find((x) => x.status === st); return h ? new Date(h.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null; };

  const restGeo = order.restaurant_geo;
  const custGeo = order.geo;
  const courierGeo = order.courier_geo;

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur-md">
        <div className="mx-auto max-w-3xl px-4 py-3 flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            {order.type === 'asporto' ? <ShoppingBag className="h-5 w-5 text-primary-foreground" /> : <Bike className="h-5 w-5 text-primary-foreground" />}
          </div>
          <div>
            <h1 className="font-display text-lg font-black leading-tight">{t(`offsite.type.${order.type}`)}</h1>
            <p className="text-xs text-muted-foreground" data-testid="order-tracking-current-status">{t(`offsite.status.${order.status}`)}</p>
          </div>
          {order.total != null && <span className="ml-auto font-display font-black text-primary tabular">{fmtEur(order.total)}</span>}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5 grid gap-5 md:grid-cols-12">
        {/* Timeline */}
        <section className="md:col-span-5">
          <div className="rounded-2xl border bg-card p-4 shadow-sm-token" data-testid="order-tracking-timeline">
            {rejected ? (
              <div className="flex items-center gap-2 text-destructive font-semibold"><PackageX className="h-5 w-5" /> {t('offsite.status.rejected')}</div>
            ) : (
              <ol className="space-y-0">
                {steps.map((st, i) => {
                  const Icon = STEP_ICON[st] || Check;
                  const done = i < currentIdx; const current = i === currentIdx;
                  const last = i === steps.length - 1;
                  return (
                    <li key={st} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`h-9 w-9 rounded-full flex items-center justify-center border transition-colors ${done || current ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-transparent'} ${current ? 'ring-2 ring-ring/30' : ''}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        {!last && <span className={`w-0.5 flex-1 min-h-[18px] ${done ? 'bg-primary' : 'bg-border'}`} />}
                      </div>
                      <div className={`pb-4 ${current ? '' : 'opacity-90'}`}>
                        <div className={`font-display font-bold text-sm ${current ? 'text-primary' : ''}`}>{t(`offsite.status.${st}`)}</div>
                        {statusAt(st) && <div className="text-xs text-muted-foreground tabular">{statusAt(st)}</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="mt-3 border-t pt-3 text-sm">
              {order.time_slot && order.time_slot !== 'asap' && (
                <div className="flex justify-between"><span className="text-muted-foreground">{t('offsite.time')}</span><span className="tabular font-medium">{order.time_slot}</span></div>
              )}
              {order.address && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{order.address}</div>}
              {(order.items || []).length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">{order.items.map((it) => `${it.qty}× ${it.name}`).join(', ')}</div>
              )}
            </div>
          </div>
        </section>

        {/* Map */}
        {order.type === 'domicilio' && custGeo && (
          <section className="md:col-span-7">
            <TrackMap
              restaurant={restGeo}
              customer={custGeo}
              courier={courierGeo}
              className="h-[320px] md:h-[420px] rounded-2xl overflow-hidden border shadow-sm-token"
            />
            {courierGeo ? (
              <p className="mt-2 text-xs text-pos-free font-medium flex items-center gap-1.5"><Bike className="h-3.5 w-3.5" /> {t('offsite.courierLive')}</p>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">{t('offsite.courierWaiting')}</p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
